import { OfferAuctionStatus, OfferStatus, Vehicle } from 'types/vehicle';
import { useAdminOfferAuction } from '.';
import { useIsAlternativeAuction } from 'hooks';
import { formatCurrency } from 'utils';

/**
 * One cell of the admin "Wants" row (AA / S / B). `isVisibleToBuyer` marks the counter the
 * buyer is currently looking at, which both the card and the table render in bold.
 */
export type WantsCell = {
  label: string;
  amount: string;
  isVisibleToBuyer: boolean;
};

export type WantsCells = {
  aa: WantsCell;
  s: WantsCell;
  b: WantsCell;
  hasAtLeastOneCounter: boolean;
};

/**
 * Single source of truth for the Wants row, shared by the grid card and the table column.
 *
 * Which counter the buyer sees:
 * - Alternative auctions (second chance / frontline): admin and seller counter through the
 *   same endpoint, so `active_alternative_offer` is whichever of them countered last, and
 *   that is the amount the buyer can Buy Now at.
 * - AutoAxess, public phase: buyers only ever see the admin's public counter. A seller
 *   public counter sits pending admin approval and is never rendered on the buyer side.
 * - AutoAxess, private phase: the top buyer sees the last counter from the seller side while
 *   it is still `sent`. That is the admin's when they overrode the seller, whose offer then
 *   becomes `admin_countered` and is filtered out of the buyer's history.
 */
export const useAdminWantsCells = (vehicle: Vehicle): WantsCells => {
  const offerAuction = useAdminOfferAuction(vehicle.id);
  const isAlternativeAuction = useIsAlternativeAuction(vehicle);

  if (isAlternativeAuction) {
    const activeOffer = vehicle.active_alternative_offer;
    const isActive = (offer: any) => {
      if (!offer || !activeOffer) return false;
      if (offer.id != null && activeOffer.id != null) return offer.id === activeOffer.id;
      return !!offer.is_admin === !!activeOffer.is_admin;
    };

    return {
      aa: {
        label: 'AA',
        amount: formatCurrency(vehicle.admin_alternative_offer?.amount ?? 0),
        isVisibleToBuyer: isActive(vehicle.admin_alternative_offer)
      },
      s: {
        label: 'S',
        amount: formatCurrency(vehicle.seller_alternative_offer?.amount ?? 0),
        isVisibleToBuyer: isActive(vehicle.seller_alternative_offer)
      },
      b: {
        label: 'B',
        amount: formatCurrency(vehicle.top_alternative_bid_offer?.amount ?? 0),
        isVisibleToBuyer: false
      },
      hasAtLeastOneCounter: !!vehicle.top_alternative_bid_offer || !!vehicle.active_alternative_offer
    };
  }

  const isPublicPhase = offerAuction?.status === OfferAuctionStatus.PUBLIC_OFFERS;

  const adminPublicCounter = offerAuction?.privateOffers?.find(el => el.is_admin && el.type === 'public');
  const aa: WantsCell = {
    label: 'AA',
    amount: formatCurrency(adminPublicCounter?.amount ?? 0),
    isVisibleToBuyer: isPublicPhase && !!adminPublicCounter
  };

  const publicSellerCounter = offerAuction?.privateOffers?.find(el => !el.is_admin && el.type === 'public');
  const s: WantsCell = (() => {
    if (publicSellerCounter) {
      return { label: 'S', amount: formatCurrency(publicSellerCounter.amount), isVisibleToBuyer: false };
    }

    const lastNonBuyerPrivateCounter = offerAuction?.privateOffers?.find(
      el => el.sender.role !== 'buyer' && el.type === 'private'
    );

    return {
      label: lastNonBuyerPrivateCounter?.is_admin ? 'AA' : 'S',
      amount: formatCurrency(lastNonBuyerPrivateCounter?.amount ?? 0),
      isVisibleToBuyer: !isPublicPhase && lastNonBuyerPrivateCounter?.status === OfferStatus.SENT
    };
  })();

  const buyerCounter = offerAuction?.privateOffers?.find(el => el.sender.role === 'buyer')?.amount;

  return {
    aa,
    s,
    b: { label: 'B', amount: formatCurrency(buyerCounter ?? 0), isVisibleToBuyer: false },
    hasAtLeastOneCounter: !!offerAuction?.privateOffers && offerAuction.privateOffers.length > 0
  };
};
